import { useCallback, useMemo } from 'react';
import { Control, Controller, UseFormSetValue, useFormState, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Label, Textarea } from '@hqo/ui-atoms';
import { CreateVisitFormV2Values, Visitor } from '../../../../types/form-data.types';
import { VisitNotificationOptIns } from '../one-visitor/one-visitor-form';
import { VisitorCsvUploadSection } from './visitor-csv-upload-section';
import { VisitStepFieldsConfig } from '../../../../hooks/use-ui-metadata-field-config.hook';
import { NoteForVisitor } from '../note-for-visitor/note-for-visitor';
import { NoteForReception } from '../note-for-reception/note-for-reception';
import { useValidationMessage } from '../../../../hooks/use-validation-message.hook';
import { useOptInAutoClear } from '../../../../hooks/use-opt-in-auto-clear.hook';

interface MultipleVisitorsFormProps {
  control: Control<CreateVisitFormV2Values>;
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
  fieldsConfig: VisitStepFieldsConfig;
}

export const MultipleVisitorsForm = ({ control, setValue, fieldsConfig }: MultipleVisitorsFormProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const { errors } = useFormState({ control });
  const visitorsWatch = useWatch({ control, name: 'visitors' });
  const visitors = useMemo(() => (visitorsWatch ?? []) as Visitor[], [visitorsWatch]);
  const canOptInEmail = useMemo(() => visitors.some((v) => Boolean(v?.email?.trim())), [visitors]);
  const canOptInSms = useMemo(() => visitors.some((v) => Boolean(v?.phone?.trim())), [visitors]);

  useOptInAutoClear(canOptInEmail, canOptInSms, setValue);

  const handleUploadSuccess = useCallback(
    (uploadedVisitors: Visitor[]) => {
      setValue('visitors', uploadedVisitors, { shouldValidate: true });
    },
    [setValue],
  );

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* Visit Summary */}
        {!fieldsConfig.visitSummary.hidden && (
          <div className="space-y-2">
            <Controller
              name="visitSummary"
              control={control}
              // eslint-disable-next-line react/jsx-no-bind
              render={({ field, fieldState }) => {
                const currentLength = field.value?.length || 0;
                const maxLength = 100;
                return (
                  <>
                    <Label htmlFor="visitSummary" className="text-sm font-medium text-foreground gap-1">
                      {formatMessage({
                        id: 'createVisitFormV2.visitStep.visitSummary',
                        defaultMessage: 'Visit summary',
                      })}
                      {fieldsConfig.visitSummary.required && <span className="text-destructive">*</span>}
                    </Label>
                    <Textarea
                      id="visitSummary"
                      placeholder={formatMessage({
                        id: 'createVisitFormV2.visitStep.placeholders.visitSummary',
                        defaultMessage: 'e.g. Quarterly business review',
                      })}
                      rows={3}
                      className={`min-h-[80px] ${fieldState.error && fieldState.isTouched ? 'border-destructive' : ''}`}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      maxLength={maxLength}
                    />
                    <div className="flex justify-between items-center min-h-[20px]">
                      {fieldState.error ? (
                        <p className="text-sm text-destructive">{formatValidationMessage(fieldState.error.message)}</p>
                      ) : (
                        <span />
                      )}
                      <p className={`text-xs ${currentLength > maxLength ? 'text-destructive' : 'text-gray-400'}`}>
                        {currentLength}/{maxLength}
                      </p>
                    </div>
                  </>
                );
              }}
            />
          </div>
        )}

        <NoteForVisitor config={fieldsConfig.checkInInstructions} />
        <NoteForReception config={fieldsConfig.noteForReception} />

        <VisitNotificationOptIns
          control={control}
          fieldsConfig={fieldsConfig}
          canOptInEmail={canOptInEmail}
          canOptInSms={canOptInSms}
          idPrefix="multiple"
          errorSms={errors.sms_opt_in}
        />
      </div>

      {/* CSV upload is hidden on mobile — not practical on small devices */}
      <div className="hidden sm:block">
        <Controller
          name="visitors"
          control={control}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ fieldState }) => (
            <VisitorCsvUploadSection
              onUploadSuccess={handleUploadSuccess}
              fieldsConfig={fieldsConfig}
              error={fieldState.error}
            />
          )}
        />
      </div>
    </div>
  );
};
